import { h } from 'preact';
import { useContext, useEffect, useState } from 'preact/hooks';

import { ExternalLinkIcon, PlaySmallIcon } from '@uw/icons';
import { headerStore } from 'global/header.store';
import { useViolationsReport } from 'hooks';
import { useViolations } from 'contexts/use-violations';
import { ApplicationContext } from 'contexts/use-application-setup';
import { redirectToDashboard } from 'helpers/redirect-to-dashboard';

import './about.scss';

// This page should be in English for all languages
export const About = () => {
  const { downloadPdfReport } = useViolationsReport();
  const { foundViolations } = useViolations();
  const { tunings } = useContext(ApplicationContext);
  const [isPlaying, setIsPlaying] = useState(false);
  const onPlay = () => {
    setIsPlaying(true);
  };

  const handleUpgradeRedirect = (event: Event) => {
    event.preventDefault();
    const redirectPath = `/widget/my-sites/p/1/site-details/overview?site=${tunings?.site_name}#pro-widget-plans`;
    redirectToDashboard(redirectPath);
  };

  useEffect(() => {
    headerStore.setBackStateToHomePage();
    headerStore.title.next(`About UserWay's AI Widget`);
  }, []);

  return (
    <div className="uwaw-about">
      <h2 className="uwaw-about__title">
        Learn how <strong>UserWay</strong> Protects your site from accessibility related lawsuits
        and ensures <strong>ADA</strong> compliance!
      </h2>
      {isPlaying ? (
        <div className="uwaw-video">
          <iframe
            className="uwaw-video__poster"
            width="100%"
            height="305"
            src="https://www.youtube.com/embed/5n6_PtaYo0s?autoplay=1&mute=1"
            title="YouTube video player - UserWay Demo"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      ) : (
        <div className="uwaw-video">
          <button
            className="uwaw-video__poster"
            type="button"
            onClick={onPlay}
            aria-label="Play UserWay Demo video"
          >
            <img
              src="./frontend/images/widget-v4.jpg"
              srcSet={`./frontend/images/widget-v4.jpg 1x, ./frontend/images/widget-v4@2x.jpg 2x`}
              alt="UserWay Demo video preview"
            />
            <span className="uwaw-video__btn">
              <PlaySmallIcon aria-hidden="true" />
            </span>
          </button>
        </div>
      )}

      <div className="uwaw-about__cta">
        <a
          href="#"
          role="link"
          target="_blank"
          rel="noopener"
          aria-label="Upgrade Now, opens in a new tab"
          className="uwaw-btn uwaw-btn_blue uwaw-btn_with-icon"
          onClick={handleUpgradeRedirect}
        >
          <span className="uwaw-about__cta-label">Upgrade Now</span>
          <ExternalLinkIcon
            className="uwaw-about__cta-icon"
            height={18}
            width={18}
            svgAttrs={{ 'aria-hidden': 'true', focusable: 'false', width: '18', height: '18' }}
          />
        </a>
        <a
          href="https://userway.org/demo/"
          target="_blank"
          rel="noopener"
          aria-label="Schedule a Demo with our Expert, opens in a new tab"
          className="uwaw-btn uwaw-btn_border uwaw-btn_with-icon"
        >
          <span className="uwaw-about__cta-label">Schedule a Demo with our Expert</span>
          <ExternalLinkIcon
            className="uwaw-about__cta-icon"
            height={18}
            width={18}
            svgAttrs={{ 'aria-hidden': 'true', focusable: 'false', width: '18', height: '18' }}
          />
        </a>
      </div>

      <p className="uwaw-about__description">
        <a
          aria-label="Download ADA accessibility report"
          role="button"
          onClick={() => downloadPdfReport()}
        >
          Download
        </a>
        &nbsp;
        <strong>ADA</strong> report showing&nbsp;
        {!!foundViolations.totalCount && (
          <span className="uwaw-about__violations">{foundViolations.totalCount}</span>
        )}
        accessibility violations
      </p>
    </div>
  );
};
